import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, Wallet, Home, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { collection, getDocs, limit, query as fsquery, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  PAYMENTS_ENABLED,
  SERVICE_CATEGORIES,
  type ServiceCategory,
  type ServiceDoc,
} from '@makhe/shared';
import { InstallAppBanner } from '@/components/pwa/InstallAppBanner';
import { BrandMark } from '@/components/brand/BrandMark';
import { ThemeToggle } from '@/components/theme/ThemeToggle';
import { LegalFooterNav } from '@/components/legal/LegalFooterNav';
import { EmptyState } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import {
  LandingShowcaseCard,
  LandingShowcaseSkeleton,
} from '@/components/landing/ShowcaseCard';
import { TrustStrip } from '@/components/landing/TrustStrip';
import { CraftChipBar } from '@/components/landing/CraftChipBar';
import { pickLandingServices, serviceToShowcase, type LandingShowcase } from '@/lib/landingShowcase';
import { MAKHE_SLOGAN } from '@/lib/brand';

function discoverPath(opts?: { q?: string; category?: ServiceCategory }): string {
  const params = new URLSearchParams();
  if (opts?.q?.trim()) params.set('q', opts.q.trim());
  if (opts?.category && SERVICE_CATEGORIES.includes(opts.category)) {
    params.set('cat', opts.category);
  }
  const qs = params.toString();
  return qs ? `/discover?${qs}` : '/discover';
}

function browseViaAuth(path: string): string {
  return `/auth/sign-up?next=${encodeURIComponent(path)}`;
}

const STEPS = [
  {
    n: '01',
    heading: 'Find someone.',
    body: 'Find someone on campus who does the thing you want.',
  },
  {
    n: '02',
    heading: 'Book a slot.',
    body: 'Book a slot between lectures. Meet at res or on campus.',
  },
  PAYMENTS_ENABLED
    ? {
        n: '03',
        heading: 'Pay in the app.',
        body: 'Pay in the app. We hold the money until you confirm the work is done.',
      }
    : {
        n: '03',
        heading: 'Agree the price.',
        body: 'The price is set when you book. You pay your creator directly.',
      },
];

const PILLARS = [
  {
    icon: Shield,
    heading: 'Student-verified bookings',
    body: 'You prove enrolment before you book. You are booking a student, not a stranger off the internet.',
  },
  PAYMENTS_ENABLED
    ? {
        icon: Wallet,
        heading: 'Held until you say it is done',
        body: 'We hold the money until you confirm the service was done. Price agreed up front. No extra buyer fee.',
      }
    : {
        icon: Wallet,
        heading: 'Price agreed up front',
        body: 'The price is locked in when you book. You pay your creator directly.',
      },
  {
    icon: Home,
    heading: 'Students earning from a skill they already have',
    body: 'Braids, nails, makeup, cuts. One student books. Another student gets paid.',
  },
];

export default function LandingPage() {
  const [query, setQuery] = useState('');
  const [live, setLive] = useState<LandingShowcase[] | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    getDocs(
      fsquery(
        collection(db, 'services'),
        where('status', '==', 'active'),
        limit(24),
      ),
    )
      .then((snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ServiceDoc);
        setLive(pickLandingServices(docs).map(serviceToShowcase));
      })
      .catch(() => setLive([]));
  }, []);

  function goToDiscover(opts?: { q?: string; category?: ServiceCategory }) {
    navigate(browseViaAuth(discoverPath(opts)));
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    goToDiscover({ q: query || undefined });
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-makhe-bg text-makhe-ink">
      <header className="sticky top-0 z-40 border-b border-makhe-line bg-makhe-bg/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <BrandMark size={32} />

          <nav className="flex items-center gap-2 text-sm sm:gap-3">
            <ThemeToggle />
            <Link
              to="/auth/sign-in"
              className="px-2 py-2 text-sm text-makhe-muted transition-colors hover:text-makhe-ink"
            >
              Sign in
            </Link>
            <Link to="/auth/sign-up" className="btn-primary px-5 py-2.5">
              Join free
            </Link>
          </nav>
        </div>
      </header>

      <InstallAppBanner />

      <main>
        <section className="brand-wash">
          <div className="mx-auto max-w-6xl px-5 pt-12 pb-16 md:pt-16 md:pb-20">
            <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:items-center md:gap-12 lg:grid-cols-[minmax(0,1fr)_460px]">
              <div className="animate-rise" style={{ animationDelay: '40ms' }}>
                <h1 className="max-w-sm">
                  <BrandMark lockup to={null} size={280} />
                  <span className="sr-only">Makhe. {MAKHE_SLOGAN}</span>
                </h1>

                <p className="mt-6 max-w-[40ch] text-base leading-relaxed text-makhe-ink/80 md:text-lg">
                  Book student braids, nails, makeup, and cuts. Other students get paid for the skill.
                </p>

                <form
                  onSubmit={handleSearch}
                  className="mt-8 flex items-center gap-2 rounded-full border border-makhe-line bg-makhe-paper px-3 py-2 shadow-soft transition-all focus-within:border-makhe-brand focus-within:ring-2 focus-within:ring-makhe-brand/20 sm:px-4 sm:py-2.5"
                >
                  <Search size={16} className="ml-1 shrink-0 text-makhe-ink" aria-hidden />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search braids, nails, fades…"
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-makhe-muted"
                    aria-label="Search services"
                  />
                  <Button type="submit" variant="ink" size="sm">
                    Browse
                  </Button>
                </form>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Link to="/auth/sign-up" className="btn-primary">
                    Join free
                  </Link>
                  <Link
                    to={browseViaAuth('/discover')}
                    className="text-sm text-makhe-muted underline-offset-4 hover:text-makhe-ink hover:underline"
                  >
                    Browse services
                  </Link>
                </div>
              </div>

              <div className="animate-rise" style={{ animationDelay: '160ms' }}>
                {live === null ? (
                  <LandingShowcaseSkeleton />
                ) : live.length === 0 ? (
                  <EmptyState
                    title="The first creators are coming."
                    description="Makhe is new. Be one of the first students to put your work in front of campus."
                    action={
                      <Link to="/auth/sign-up" className="btn-primary">
                        Start selling <ArrowRight size={13} />
                      </Link>
                    }
                  />
                ) : (
                  <>
                    <p className="eyebrow mb-3">Live listings</p>
                    <div className={live.length === 1 ? 'md:max-w-[240px]' : undefined}>
                      <div className={live.length === 1 ? undefined : 'grid grid-cols-2 gap-3'}>
                        {live.map((card) => (
                          <LandingShowcaseCard key={card.id} card={card} />
                        ))}
                      </div>
                      <Link
                        to={browseViaAuth('/discover')}
                        className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg border border-makhe-line bg-makhe-paper py-3 text-xs font-medium text-makhe-muted transition-colors hover:border-makhe-primary/40 hover:text-makhe-primary"
                      >
                        See all creators <ArrowRight size={13} />
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        <CraftChipBar onPick={goToDiscover} />

        <TrustStrip />

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="mb-10 flex items-baseline justify-between border-b border-makhe-line pb-4">
            <p className="eyebrow">How it works</p>
            <p className="font-display text-sm italic text-makhe-muted">three steps</p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            {STEPS.map((step, i) => (
              <div key={step.n} className="animate-rise" style={{ animationDelay: `${i * 100}ms` }}>
                <p
                  className="font-display text-6xl leading-none text-makhe-primary/30"
                  style={{ fontVariationSettings: '"opsz" 144, "wght" 300' }}
                >
                  {step.n}
                </p>
                <h3 className="mt-4 font-display text-xl">{step.heading}</h3>
                <p className="mt-3 text-sm leading-relaxed text-makhe-ink/70">{step.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="border-y border-makhe-line bg-makhe-paper">
          <div className="mx-auto grid max-w-6xl divide-y divide-makhe-line md:grid-cols-3 md:divide-x md:divide-y-0">
            {PILLARS.map(({ icon: Icon, heading, body }) => (
              <div key={heading} className="flex gap-5 p-8 md:p-10">
                <div className="mt-0.5 shrink-0 rounded-md bg-makhe-primary/10 p-2.5">
                  <Icon size={20} className="text-makhe-primary" />
                </div>
                <div>
                  <h3 className="font-semibold leading-tight">{heading}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-makhe-ink/70">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20 md:grid md:grid-cols-12 md:items-center md:gap-12">
          <blockquote className="md:col-span-8">
            <p
              className="font-display text-2xl leading-snug text-makhe-ink md:text-4xl"
              style={{ fontVariationSettings: '"opsz" 144, "wght" 350' }}
            >
              <span className="text-makhe-primary text-4xl">&ldquo;</span>
              We built Makhe so the student in 314 can braid between exams, and the student
              in 211 can book them without a random WhatsApp group.
              <span className="text-makhe-primary text-4xl">&rdquo;</span>
            </p>
          </blockquote>
          <div className="mt-8 md:col-span-4 md:mt-0">
            <p className="eyebrow">A note from the founder</p>
            <p className="mt-3 font-display text-xl italic">Nompumelelo</p>
          </div>
        </section>

        <section className="border-t border-makhe-line bg-makhe-paper">
          <div className="mx-auto max-w-xl px-5 py-24 text-center">
            <p className="eyebrow justify-center">Free to join</p>
            <h2
              className="mt-6 font-display text-4xl tracking-tight md:text-6xl"
              style={{ fontVariationSettings: '"opsz" 144, "wght" 350' }}
            >
              Find a student. Book. Or get paid for your skill.
            </h2>
            <p className="mt-5 text-sm text-makhe-muted">
              Join as a buyer to book services, or as a seller to earn from your craft.
            </p>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Link to={browseViaAuth('/discover')} className="btn-primary px-8 py-4">
                Find a creator <ArrowRight size={14} />
              </Link>
              <Link to={browseViaAuth('/seller/onboarding')} className="btn-secondary px-8 py-4">
                Sell my craft <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-makhe-line">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-8">
          <div>
            <BrandMark size={28} to={null} />
            <p className="mt-2 text-[11px] uppercase tracking-widest text-makhe-muted">
              {MAKHE_SLOGAN}
            </p>
          </div>
          <LegalFooterNav />
          <p className="text-[11px] uppercase tracking-widest text-makhe-muted">
            © {new Date().getFullYear()} Makhe Technologies
          </p>
        </div>
      </footer>
    </div>
  );
}
